import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import { Button, Drawer, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import { InputOptionComponent } from "components";
import { useLayout } from "components/layout/LayoutComponentContext";
import { useSnackbar } from "components/snackbar/SnackbarContext";
import { allowedRoleSelect, externalRoles, internalRoles, statusOptions } from "consts/UserManagement";
import { getExternalAccountDetail, getInternalAccountDetail, updateAccountExternal, updateAccountInternal } from "features/account/service/AccountProfileService";
import React, { useEffect, useState } from 'react'
import storage from "services/storage";
import CompanyDocumentAccessSettings from "./CompanyDocumentAccessSettings";


const UserManagementEditAccount = ({ isOpen, onClose, onBack, currentEditUser, user }) => {
    const typeStorage = storage.getItem("type");
    const rolesToDisplay = typeStorage === "CLIENT" ? externalRoles : internalRoles
    const [email, setEmail] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [role, setRole] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [fullName, setFullName] = useState("");
    const { setMessage } = useSnackbar();
    const [profilePictureUrl, setProfilePictureUrl] = useState("")
    const [status, setStatus] = useState("")
    const [previousData, setPreviousData] = useState(null)
    const { refetchUserBasedOnType } = useLayout()

    const getChangedFields = (prevData, currData) => {
        return Object.fromEntries(
            Object.entries(currData).filter(
                ([key, value]) => prevData[key] !== value,
            ),
        );
    };

    useEffect(() => {
        if (currentEditUser) {
            getUserDetail(currentEditUser?.id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentEditUser]);

    const getUserDetail = async (id) => {
        const response = typeStorage === "CLIENT" ? await getExternalAccountDetail(id) : await getInternalAccountDetail(id);
        if (Object.keys(response).length !== 0) {
            setEmail(response.data.email);
            setPhoneNumber(response.data.phone_number.replace("+62", ""));
            setRole(response.data.role);
            setFullName(response.data.full_name);
            setProfilePictureUrl(response.data.avatar_path);
            setStatus(response.data.status)
            setPreviousData(response.data);
        }
    }


    const submit = async () => {
        setSubmitted(true);
        if (!email || !fullName || !phoneNumber || !role) {
            setMessage({
                title: "Failed to edit",
                desc: "Please fill in all fields",
                type: "error",
            });
            return;
        }
        let response = null
        if (typeStorage === "CLIENT") {
            const data = {
                email: email,
                full_name: fullName,
                phone_number: "+62" + phoneNumber,
                role: role,
                status: status,
            };
            const sanitizedData = getChangedFields(previousData, data);
            response = await updateAccountExternal(currentEditUser?.id, sanitizedData)
        } else {
            const data = {
                email: email,
                full_name: fullName,
                phone_number: "+62" + phoneNumber,
                role: role,
                status: status,
            };
            const sanitizedData = getChangedFields(previousData, data);
            response = await updateAccountInternal(currentEditUser?.id, sanitizedData)
        }

        if (response?.info?.title) {
            setMessage({
                title: "Success",
                desc: "Account edited",
                type: "success",
            });
            setSubmitted(false);
            refetchUserBasedOnType()
            onBack();
        } else {
            setMessage({
                title: "Failed",
                desc: response,
                type: "error",
            });
        }
    };

    return (
        <Drawer
            className="account-profile-drawer"
            anchor="right"
            open={isOpen}
            onClose={onClose}
        >
            <Stack className="header" direction="row" justifyContent="space-between" alignItems="center">
                <img alt='close-profile-icon' className='cursor-pointer' src='/images/sidebar/sidebar-back.svg' onClick={onBack} />
                <p className='account-profile-title'>Edit Account</p>
                <img alt='close-profile-icon' className='cursor-pointer' src='/images/close-profile-icon.svg' onClick={onClose} />
            </Stack>
            <Stack className='container' spacing={3}>
                <Stack alignItems='center'>
                    {profilePictureUrl ? (
                        <img
                            alt='account-profile'
                            className='w-40 h-40 rounded-full shrink-0 object-cover'
                            src={profilePictureUrl}
                        />
                    ) : (
                        <AccountCircleRoundedIcon
                            viewBox='2 2 20 20'
                            aria-label='account-profile-image-default'
                            className='p-0'
                            sx={{ height: "160px", width: "160px", color: "#ccc" }}
                        />
                    )}
                </Stack>
                <Stack spacing={1}>
                    <Typography fontSize={12}>Full name</Typography>
                    <TextField
                        error={submitted && !fullName}
                        name='name'
                        placeholder="Full name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                    />
                </Stack>
                <Stack spacing={1}>
                    <Typography fontSize={12}>E-mail address</Typography>
                    <TextField
                        error={submitted && !email}
                        name='email'
                        placeholder="E-mail address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </Stack>
                <Stack spacing={1}>
                    <Typography fontSize={12}>Phone number</Typography>
                    <TextField
                        error={submitted && !phoneNumber}
                        placeholder="Phone number"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment
                                    sx={{
                                        backgroundColor: "#EEF1F8",
                                        padding: "20px 14px",
                                        borderRight: "1px solid #727273",
                                        borderTopLeftRadius: "10px",
                                        borderBottomLeftRadius: "10px",
                                    }}
                                    position='start'
                                >
                                    <p className='account-edit-profile-form-input-label'>+62</p>
                                </InputAdornment>
                            ),
                        }}
                        name='phone'
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                </Stack>
                <Stack spacing={1}>
                    <Typography fontSize={12}>Role</Typography>
                    <InputOptionComponent
                        disabled={!allowedRoleSelect.includes(user.role)}
                        width='full'
                        label='Select role'
                        options={rolesToDisplay}
                        value={rolesToDisplay?.find(
                            (el) => el?.value === role,
                        )}
                        onSelect={(opt) =>
                            setRole(opt?.value)
                        }
                    />
                </Stack>
                <Stack spacing={1}>
                    <Typography fontSize={12}>Status</Typography>
                    <InputOptionComponent
                        width='full'
                        label='Select status'
                        options={statusOptions}
                        value={statusOptions?.find(
                            (el) => el?.value === status,
                        )}
                        onSelect={(opt) =>
                            setStatus(opt?.value)
                        }
                    />
                </Stack>
                {typeStorage === "ADMIN" && user?.role === "SUPER ADMIN" && currentEditUser?.id && (
                    <CompanyDocumentAccessSettings key={currentEditUser.id} adminId={currentEditUser.id} />
                )}
                <Stack spacing={2} direction='row' justifyContent='flex-end'>
                    <Button color='secondary' onClick={onBack} variant='outlined'>
                        <Typography fontSize={12}>Cancel</Typography>
                    </Button>
                    <Button
                        onClick={submit}
                        variant='contained'
                        className='!bg-[#556FB5]'
                    >
                        <Typography fontSize={12}>Save Changes</Typography>
                    </Button>
                </Stack>
            </Stack>
        </Drawer>
    )
}

export default UserManagementEditAccount